/**
 * The dashboard's "how I see data" preferences — one reactive blob, server-backed.
 *
 * Holds the knobs the Settings page edits:
 *   - `writeScope`   — per-resource default VISIBILITY for a NEW overlay/attach.
 *   - `resolveScope` — per-resource default tenancy tier a RESOLVED view opens at.
 *   - `txFeed`       — the Transactions explorer's default feed scope.
 *   - `transport`    — REST vs GraphQL per data operation, plus the master switch.
 *
 * Source of truth is the reserved `__prefs:dashboard` saved-view surface, one
 * row per LAYER, blob under `view_state.extra`:
 *
 *   - `user`   — the caller's own overrides. Follows them across devices.
 *   - `tenant` — the workspace default an admin publishes for everyone.
 *   - `system` — a fleet-wide default (internal-only to write; the rung a plan
 *                entitlement would occupy).
 *
 * The layers are kept SEPARATE, in state and in storage, and collapsed only at
 * read time by the shared resolver (`effectiveScopes`). They used to be picked —
 * "my row, else the tenant row" — and the pick is what made central management
 * not work: the chosen blob became local state, the first change to ANY setting
 * persisted the whole blob into the caller's own row, and from then on every
 * value inherited from the workspace was frozen as if the user had chosen it.
 * A later change by the admin reached nobody who had ever opened Settings, every
 * inherited row rendered as a personal override, and "reset to default" reset
 * past the workspace to the product baseline.
 *
 * So: writes here only ever touch the caller's OWN row; publishing the workspace
 * layer is {@link DashboardPrefs.publishWorkspaceDefaults}, gated by the caller
 * (`saved-views:create`, the same verb that gates publishing any shared view).
 *
 * `ScopedStorage` stays the SYNCHRONOUS local cache — of ALL the layers, since a
 * first paint that cached only the user's row would show the product baseline
 * where the workspace has said otherwise. It seeds the first paint
 * (and keeps working offline / pre-auth), and every change writes through BOTH
 * the cache and the server. The server write is best-effort — the UI always
 * reads the local state/cache, so a failed/absent network round-trip never
 * blocks a setting from taking effect.
 *
 * `useScopePrefs` is the tenancy facade over this store (it implements the shared
 * `ScopePrefsStore`), so
 * their existing call sites (catalog adapter, graph assignment, resolve hooks,
 * Settings, the explorer) are unchanged.
 *
 * Transport used to be excluded, on the reasoning that a setting deciding WHICH
 * API a read uses cannot itself depend on a network read. The premise is true
 * and the conclusion does not follow: this store is REST-only, so there is no
 * circularity; it seeds synchronously from the cache; and the transport registry
 * answers with its own default from the first frame, network or no network. What
 * the exclusion actually cost was central management — the choice lived in raw
 * browser localStorage, where no admin could set it, no plan could shape it, and
 * support could not read it back. It is a layer here like every other one; what
 * stays on localStorage is the `?debug=1` OVERRIDE, which is a diagnostic.
 *
 * `app/transportPolicy` is the seam that carries these layers onto
 * `FeatureFlags`' policy layer, where every adapter already reads them.
 */
import * as React from "react";
import type {
  AttributionReadScope,
  AttributionResource,
  AttributionScope,
  InheritedScopeSource,
  ManagedScopeDefaults,
} from "@authe/fe-commons/attribution";
import { TRANSPORT_MASTER_DEFAULT, transportDefaultFor } from "@/config/transport";
import type { FlagMap } from "@authe/fe-commons/flags";
import type { DataOperation, Transport } from "@authe/fe-commons/transport";
import type { TxTenancyScopeId } from "@/features/transactions/scopes";
import {
  EMPTY_BLOB,
  transportFallback,
  type DashboardPrefsBlob,
  type DashboardPrefsSection,
} from "./dashboardPrefs.blob";

export type {
  DashboardPrefsBlob,
  DashboardPrefsCache,
  DashboardPrefsSection,
  ManagedPrefsLayer,
  TransportPrefs,
  WriteScopeMap,
  ResolveScopeMap,
} from "./dashboardPrefs.blob";

export {
  blobIsEmpty,
  inheritedTransportFor,
  inheritedTransportMaster,
  MANAGED_PREFS_LAYERS,
  managedScopeDefaults,
  managedTxFeed,
  normalizeBlob,
  normalizeCache,
  sectionIsEmpty,
  transportFallback,
  transportLayerSource,
  transportMasterFallback,
  transportPolicyFlags,
  withSection,
} from "./dashboardPrefs.blob";

export interface DashboardPrefs {
  workspaceGuide?: import("@authe/ui-core/tenant").WorkspaceGuide;
  /** The caller's OWN overrides. Never null; empty means "inherit everything". */
  blob: DashboardPrefsBlob;
  homeDashboardIds: string[];
  setHomeDashboardIds: (ids: string[] | null) => void;
  /**
   * The layers beneath {@link blob}, least specific first.
   *
   * Handed to the shared resolver rather than merged here: which layer wins per
   * resource, and what happens when one names a rung the policy has withdrawn,
   * is the same question for every app that has these settings, and this one
   * does not get its own answer to it.
   */
  managed: ManagedScopeDefaults[];
  /** The Transactions feed default the layers imply, or `null` for the product one. */
  managedTxFeed: TxTenancyScopeId | null;
  /** The workspace layer as stored, for the surface that edits it. */
  workspaceDefaults: DashboardPrefsBlob | null;
  /** Set (or clear with `null`) a per-resource default write scope. */
  setWriteScope: (resource: AttributionResource, scope: AttributionScope | null) => void;
  /** Set (or clear with `null`) a per-resource default resolve tier. */
  setResolveScope: (resource: AttributionResource, scope: AttributionReadScope | null) => void;
  /** Set (or clear with `null`) the Transactions feed default. */
  setTxFeed: (scope: TxTenancyScopeId | null) => void;
  /**
   * Every transport layer collapsed into the `FlagMap` the flag store's POLICY
   * layer takes — plan, then workspace, then the caller's own.
   *
   * Handed out already merged, unlike {@link managed}, because a flag map has no
   * per-key precedence to preserve: whoever consumes it asks about ONE flag and
   * wants the answer, not the provenance. Provenance is
   * {@link transportFallback}.
   */
  transportPolicy: FlagMap;
  /**
   * What stands for `op` when this caller chooses nothing: the transport AND
   * which layer states it (`source: null` = only the product default does).
   *
   * One answer rather than a source and a value fetched separately, because a
   * row that pairs them itself gets them from different places — which is
   * exactly what happened: the label named the REGISTRY's default while
   * crediting the workspace, so a row read "Default (REST — set by your
   * workspace)" after the workspace published GraphQL.
   */
  transportFallback: (op: DataOperation) => {
    transport: Transport;
    source: InheritedScopeSource | null;
  };
  /** The same, for the master GraphQL switch. */
  transportMasterFallback: () => { master: boolean; source: InheritedScopeSource | null };
  /** Set (or clear with `null`) the caller's own transport for one operation. */
  setTransport: (op: DataOperation, transport: Transport | null) => void;
  /** Set (or clear with `null`) the caller's own master GraphQL switch. */
  setTransportMaster: (enabled: boolean | null) => void;
  streamingEnabled: boolean;
  setStreamingEnabled: (enabled: boolean | null) => void;
  /**
   * Publish one SECTION of `blob` as the WORKSPACE default — the layer everyone
   * in the tenant inherits where they have not chosen for themselves.
   *
   * Per section, not per blob: there is one row per layer, so a whole-blob
   * publish replaced everything the row said with what this admin's own row
   * happens to say. An admin who INHERITED a colleague's overlay-visibility
   * defaults (and so states nothing about them) withdrew them for the entire
   * tenant by changing one API preference. See {@link DashboardPrefsSection}.
   *
   * Rejects rather than throwing into a render. The caller gates the affordance
   * on `saved-views:create`; the API gates the write again
   * (`SavedViewService._guard_scope`), because a UI gate is a courtesy and the
   * scope tag is the rule.
   */
  publishWorkspaceDefaults: (
    section: DashboardPrefsSection,
    blob: DashboardPrefsBlob,
  ) => Promise<void>;
  /**
   * Withdraw one section of the workspace layer, returning everyone to the tier
   * below it for those settings. The row itself is deleted once nothing is left
   * in it, so "no workspace default is published" stays a reachable state.
   */
  clearWorkspaceDefaults: (section: DashboardPrefsSection) => Promise<void>;
  /**
   * The last server write or hydration that failed, or `null` when the stored
   * copy is in step.
   *
   * These preferences decide who a NEW overlay is visible to, so "it looked
   * applied here and never reached the server" is an evidentiary problem, not a
   * cosmetic one: the same analyst on another machine would attach labels at a
   * different visibility than the one they chose. The provider raises it once
   * per failure; Settings renders it beside the control.
   */
  syncError: unknown;
  /** Retry what failed — read the rows again, or push the local copy again — clearing {@link syncError} on success. */
  retrySync: () => void;
}

/** Provider-less fallback: in-memory empties, no persistence (tests / isolated renders). */
const FALLBACK: DashboardPrefs = {
  blob: EMPTY_BLOB,
  homeDashboardIds: [],
  setHomeDashboardIds: () => {},
  managed: [],
  managedTxFeed: null,
  workspaceDefaults: null,
  setWriteScope: () => {},
  setResolveScope: () => {},
  setTxFeed: () => {},
  transportPolicy: {},
  transportFallback: (op: DataOperation) => ({ transport: transportDefaultFor(op), source: null }),
  transportMasterFallback: () => ({ master: TRANSPORT_MASTER_DEFAULT, source: null }),
  setTransport: () => {},
  setTransportMaster: () => {},
  streamingEnabled: true,
  setStreamingEnabled: () => {},
  publishWorkspaceDefaults: () => Promise.resolve(),
  clearWorkspaceDefaults: () => Promise.resolve(),
  syncError: null,
  retrySync: () => {},
};

export const DashboardPrefsContext = React.createContext<DashboardPrefs | null>(null);

/** Access the raw dashboard prefs store (prefer the `useScopePrefs` facade). */
export function useDashboardPrefs(): DashboardPrefs {
  return React.useContext(DashboardPrefsContext) ?? FALLBACK;
}
